import * as state from '@/components/Analysis/state';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors } from '@/theme/colors';
import { Stack, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { ReviewActionChecklistConfig } from '@/__generated__/graphql';
import { generatePoiDisplayName } from '../../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useComponentGroupTitles } from '../../../ReviewScreen/hooks/useComponentGroupTitles';
import { filterReviewActionsWithPois } from '../../../ReviewScreen/hooks/useFilterReviewActionPois';
import { useEquipmentList, usePointOfInterestDetails } from '../../hooks';
import {
  CurrentItemIcon,
  EquipmentName,
  NavigationContainer,
  SectionItem,
  SectionTitle,
} from '../styles';
import { CustomSkeleton } from './styles';

const scrollToElement = (id: string) => {
  const element = document.querySelector(`#${CSS.escape(id)}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const ListSkeleton = () => {
  return (
    <Stack gap={2}>
      <CustomSkeleton
        animation="wave"
        variant="text"
        width={90}
        height={24}
        sx={{ marginLeft: 4 }}
      />
      <Stack marginBottom="4px" marginLeft={5} gap={1.5}>
        <CustomSkeleton animation="wave" variant="text" width={90} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={60} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={70} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={50} height={16} />
      </Stack>
    </Stack>
  );
};

const NavigatorSkeleton = () => {
  return (
    <Stack gap={6} marginBottom={6}>
      <Stack direction="row" alignItems="center" paddingX={3}>
        <CustomSkeleton animation="wave" variant="text" width={200} height={30} />
      </Stack>
      {Array.from({ length: 2 }).map((_, index) => (
        <ListSkeleton key={index} />
      ))}
    </Stack>
  );
};

export const ComponentReportNavigator = () => {
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const selectedReport = useAtomValue(state.selectedReport);
  const equipmentList = useEquipmentList(selectedReport);
  const groupTitles = useComponentGroupTitles();
  const navbarHeight = useNavbarHeight();
  const { groupPoiByEquipmentWithNumbers: equipmentAndPOIs } = usePointOfInterestDetails();

  useEffect(() => {
    if (Object.keys(equipmentList).length > 0 && !selectedItem) {
      setSelectedItem(Object.keys(equipmentList)[0]);
    }
  }, [equipmentList, selectedItem]);

  const handleClick = (item: string) => {
    setSelectedItem(item);
    scrollToElement(item);
  };

  const isLoading = useMemo(() => {
    // For component report navigation we only depend on the report
    // and precomputed groupTitles; the global assemblyTemplate atom
    // is not required here.
    return !groupTitles || !selectedReport;
  }, [groupTitles, selectedReport]);

  const specifications = useMemo(() => {
    // Step 1: Get checklistConfig from template
    const checklistConfig =
      (selectedReport?.components?.[0]?.template
        ?.reviewActionChecklistConfig || []) as ReviewActionChecklistConfig[];

    // Step 2: Get reviewActionPois from report (GraphQL returns JSON/any for this field)
    const reviewActionPois = (selectedReport as { reviewActionPois?: Record<string, unknown[]> | null })
      ?.reviewActionPois ?? undefined;

    // Step 3: Use filterReviewActionsWithPois to get only config items with POIs
    const configsWithPois = filterReviewActionsWithPois(checklistConfig, reviewActionPois);

    // Step 4: Map to navbar items (title, id)
    const reviewActionChecklists = configsWithPois.map((config: ReviewActionChecklistConfig) => ({
      title: config.displayName,
      id: config.reviewActionValue,
    }));

    // Step 5: Return combined with groupTitles and Attachments
    return [
      ...(groupTitles || []),
      ...reviewActionChecklists,
      {
        title: 'Attachments',
        id: 'attachments',
      },
    ];
  }, [groupTitles, selectedReport]);

  const hasData = useMemo(() => {
    return !!selectedReport?.pointOfInterests?.length || !!selectedReport?.components?.length;
  }, [selectedReport]);

  return (
    <NavigationContainer navbarHeight={navbarHeight} data-testId="report-navigation">
      <Typography
        color={abyssColors.primary[700]}
        fontWeight={700}
        m={3}
        data-testid="report-navigator-title"
      >
        Contents
      </Typography>
      {isLoading && hasData && <NavigatorSkeleton />}
      {!isLoading &&
        hasData &&
        equipmentList.map((equipment) => (
          <>
            <Stack gap={2} marginBottom={6}>
              <Stack direction="row" alignItems="center">
                <CurrentItemIcon selected={selectedItem === equipment?.tagName} />
                <EquipmentName onClick={() => handleClick(equipment?.tagName ?? '')} marginLeft={1}>
                  {equipment?.tagName ?? ''}
                </EquipmentName>
              </Stack>
              <Stack data-testid="report-navigator-equipment">
                <SectionTitle selected>Specifications</SectionTitle>
                {specifications?.map((detail) => (
                  <Stack
                    key={detail.title}
                    direction="row"
                    alignItems="center"
                    marginBottom="4px"
                    data-testid="report-navigator-detail"
                  >
                    <CurrentItemIcon
                      selected={selectedItem === `${equipment?.tagName ?? ''}-${detail.title}`}
                    />
                    <SectionItem
                      selected={selectedItem === `${equipment?.tagName ?? ''}-${detail.title}`}
                      onClick={() => {
                        handleClick(`${equipment?.tagName ?? ''}-${detail.title}`);
                      }}
                    >
                      {detail.title}
                    </SectionItem>
                  </Stack>
                ))}
                {equipmentAndPOIs[equipment?.tagName ?? '']?.length > 0 && (
                  <SectionTitle selected>Point of interests</SectionTitle>
                )}
                {equipmentAndPOIs[equipment?.tagName ?? '']?.map((point) => (
                  <Stack
                    key={point.id}
                    direction="row"
                    alignItems="center"
                    marginBottom="4px"
                    data-testid="report-navigator-poi"
                  >
                    <CurrentItemIcon selected={selectedItem === point.id} />
                    <SectionItem
                      selected={selectedItem === point.id}
                      onClick={() => {
                        handleClick(point.id);
                      }}
                    >
                      {generatePoiDisplayName({
                        name: point.name,
                        poiType: point.type,
                        poiId: point.poiId,
                      })}
                    </SectionItem>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </>
        ))}
    </NavigationContainer>
  );
};
